import React, { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import FinanzChart from "./FinanzChart";
import BudgetVerwaltung from "./BudgetVerwaltung";
import CashflowPlanung from "./CashflowPlanung";
import CashflowSzenarien from "./CashflowSzenarien";
import BudgetPage from "./BudgetPage";
import BookingMatchingPopup from "./BookingMatchingPopup";
import ProjectsFinanceDashboard from "./ProjectsFinanceDashboard";
import { getBudgetProjections } from "../services/azureBudgetService";
import { uploadBookings, getBookings } from "../services/azureBookingService"; // Neuer Service

const Finanzen = ({ initialTab }) => {
  const [activeTab, setActiveTab] = useState(initialTab || "cashflow");

  // Sync tab with URL
  useEffect(() => {
    if (initialTab && initialTab !== activeTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const [bookings, setBookings] = useState([]);
  const [budgetProjections, setBudgetProjections] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalEntries: 0,
    dateRange: "",
    importDate: null,
    newEntries: 0,
    duplicates: 0,
  });
  const [showMatchingPopup, setShowMatchingPopup] = useState(false);
  const [matchableBookings, setMatchableBookings] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Lade sowohl bestehende Buchungen als auch Budget-Projektionen
      const [existingBookings, projections] = await Promise.all([
        loadExistingBookings(),
        loadBudgetProjections(),
      ]);

      setBookings(existingBookings);
      setBudgetProjections(projections);

      // Berechne Statistiken für bestehende Daten
      if (existingBookings.length > 0) {
        updateStats(existingBookings);
      }
    } catch (error) {
      console.error("Fehler beim Laden der initialen Daten:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadExistingBookings = async () => {
    try {
      const azureBookings = await getBookings();

      // Transformiere Azure-Daten zu dem Format, das der Chart erwartet
      return azureBookings.map((booking) => ({
        Nr: booking.Nr,
        Valuta: new Date(booking.Valuta),
        Konto: booking.Konto,
        Gegenkonto: booking.Gegenkonto,
        Soll: booking.Soll,
        Haben: booking.Haben,
        Betrag: booking.Betrag,
        Buchungstext: booking.Buchungstext,
        KontoBezeichnung: booking.KontoBezeichnung,
        GegenkontoBezeichnung: booking.GegenkontoBezeichnung,
      }));
    } catch (error) {
      console.error("Fehler beim Laden der Buchungen:", error);
      return [];
    }
  };

  const loadBudgetProjections = async () => {
    try {
      // Lade Budget-Projektionen für die nächsten 12 Monate
      const startDate = new Date().toISOString().split("T")[0];
      const endDate = new Date();
      endDate.setFullYear(endDate.getFullYear() + 1);

      const projections = await getBudgetProjections(
        startDate,
        endDate.toISOString().split("T")[0]
      );
      return projections;
    } catch (error) {
      console.error("Fehler beim Laden der Budget-Projektionen:", error);
      return [];
    }
  };

  const updateStats = (bookingData, uploadResult = null) => {
    const sortedDates = bookingData.map((b) => b.Valuta).sort((a, b) => a - b);

    setStats({
      totalEntries: bookingData.length,
      dateRange:
        sortedDates.length > 0
          ? `${sortedDates[0].toLocaleDateString("de-CH")} - ${sortedDates[
              sortedDates.length - 1
            ].toLocaleDateString("de-CH")}`
          : "",
      importDate: new Date(),
      newEntries: uploadResult?.new || 0,
      duplicates: uploadResult?.duplicates || 0,
    });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      // Upload zu Azure Table Storage
      const uploadResult = await uploadBookings(file);

      // Lade aktualisierte Buchungen
      const updatedBookings = await loadExistingBookings();
      setBookings(updatedBookings);

      // Aktualisiere Statistiken
      updateStats(updatedBookings, uploadResult);

      event.target.value = ""; // Reset file input

      // Filtere Kundenzahlungen (Konto 1020, Gegenkonto 1100)
      const incomingPayments = updatedBookings.filter(
        (booking) =>
          booking.Konto === 1020 &&
          booking.Gegenkonto === 1100 &&
          booking.Betrag > 0
      );

      // Zeige Matching-Popup wenn es neue Kundenzahlungen gibt
      if (incomingPayments.length > 0 && uploadResult.new > 0) {
        setMatchableBookings(incomingPayments);
        setShowMatchingPopup(true);
      } else {
        // Zeige Erfolgs-Nachricht
        alert(
          `Upload erfolgreich!\nNeu: ${uploadResult.new}\nDuplikate übersprungen: ${uploadResult.duplicates}`
        );
      }
    } catch (error) {
      console.error("Fehler beim Upload:", error);
      alert("Fehler beim Upload der Excel-Datei: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleMatchingComplete = () => {
    setShowMatchingPopup(false);
    setMatchableBookings([]);
    // Budget-Daten neu laden
    handleBudgetDataChange();
  };

  const handleMatchSuccess = (matchData) => {
    console.log("Matched:", matchData);
    // Optional: Toast-Nachricht oder ähnliches
  };

  const handleBudgetDataChange = async () => {
    // Callback für Budget-Verwaltung um Projektionen neu zu laden
    const projections = await loadBudgetProjections();
    setBudgetProjections(projections);
  };

  if (loading && bookings.length === 0) {
    return (
      <div className="finanzen">
        <div className="finanzen-loading">Lade Finanzdaten...</div>
      </div>
    );
  }

  return (
    <div className="finanzen">
      {activeTab === "overview" && (
        <div className="finanzen-overview">
          {/* Import Statistiken */}
          {stats.totalEntries > 0 && (
            <div className="finanzen-import-stats">
              {stats.importDate && (
                <div className="finanzen-last-import">
                  Letzter Import: {stats.importDate.toLocaleString("de-CH")}
                </div>
              )}
            </div>
          )}

          {/* Chart Container */}
          <div className="finanzen-chart-container">
            {bookings.length === 0 ? (
              <div className="finanzen-no-data">
                {loading
                  ? "Lade Buchungsdaten..."
                  : "Keine Buchungsdaten verfügbar. Bitte unter 'Budget Verwaltung' Excel Journal hochladen."}
              </div>
            ) : (
              <FinanzChart
                bookings={bookings}
                budgetProjections={budgetProjections}
              />
            )}
          </div>
        </div>
      )}

      {activeTab === "cashflow" && (
        <div className="finanzen-cashflow">
          <CashflowPlanung />
        </div>
      )}

      {activeTab === "szenarien" && (
        <div className="finanzen-szenarien">
          <CashflowSzenarien />
        </div>
      )}

      {activeTab === "projects" && (
        <div className="finanzen-projects">
          <ProjectsFinanceDashboard />
        </div>
      )}

      {activeTab === "budget" && (
        <div className="finanzen-budget">
          <BudgetVerwaltung onDataChange={handleBudgetDataChange} />
        </div>
      )}

      {activeTab === "auswertung" && (
        <div className="finanzen-auswertung-tab">
          <BudgetPage />
        </div>
      )}

      {showMatchingPopup && (
        <BookingMatchingPopup
          bookings={matchableBookings}
          onClose={handleMatchingComplete}
          onMatch={handleMatchSuccess}
        />
      )}
    </div>
  );
};

export default Finanzen;
